/**
 * Financial Workspace — three-mode executive planning environment.
 *
 * Modes:
 *   Intelligence  — AI briefing + KPIs + variances + Ask Anything (default)
 *   Exploration   — drill-down into metrics + accounts + JEs (F6)
 *   Modeling      — scenario planning + projections (F7)
 */

import { useState, useCallback } from 'react'
import { cn } from '../lib/utils'
import { Brain, Search, Calculator } from 'lucide-react'
import PeriodSelector from '../components/PeriodSelector'
import EntitySelector from '../components/EntitySelector'
import { IntelligenceMode, ModelingMode } from '../components/workspace'
import { ExplorationMode } from '../components/workspace/ExplorationMode'

type WorkspaceMode = 'intelligence' | 'exploration' | 'modeling'

const MODES: { key: WorkspaceMode; label: string; icon: typeof Brain }[] = [
  { key: 'intelligence', label: 'Intelligence', icon: Brain },
  { key: 'exploration', label: 'Exploration', icon: Search },
  { key: 'modeling', label: 'Modeling', icon: Calculator },
]

export default function FinancialWorkspace() {
  const [activeMode, setActiveMode] = useState<WorkspaceMode>('intelligence')
  const [entityCode, setEntityCode] = useState('*')
  const [periodCode, setPeriodCode] = useState('')
  const [comparePeriod, setComparePeriod] = useState('')
  const [explorationMetric, setExplorationMetric] = useState<string | null>(null)

  const switchToExploration = useCallback((metricCode: string) => {
    setExplorationMetric(metricCode)
    setActiveMode('exploration')
  }, [])

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-lg font-semibold text-[var(--text-primary,#F0F2F5)]">
          Financial Workspace
        </h1>

        {/* Selectors */}
        <div className="flex items-center gap-2.5">
          <EntitySelector value={entityCode} onChange={setEntityCode} />
          <PeriodSelector value={periodCode} onChange={setPeriodCode} />
          {activeMode !== 'modeling' && (
            <>
              <span className="text-[10px] text-[var(--text-hint,#475569)] uppercase">vs</span>
              <PeriodSelector value={comparePeriod} onChange={setComparePeriod} defaultToPrior />
            </>
          )}
        </div>
      </div>

      {/* Mode Switch */}
      <div className={cn(
        'flex items-center gap-1 p-1 rounded-lg mb-6',
        'bg-[var(--bg-surface,var(--bg-surface))] border border-white/[0.06]',
        'w-fit',
      )}>
        {MODES.map(({ key, label, icon: Icon }) => {
          const isActive = activeMode === key
          return (
            <button
              key={key}
              onClick={() => setActiveMode(key)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-md text-[12px] font-medium',
                'transition-all duration-150',
                isActive
                  ? 'bg-[var(--accent,var(--accent))] text-white shadow-sm'
                  : 'text-[var(--text-muted,#64748B)] hover:text-[var(--text-secondary,#94A3B8)] hover:bg-white/[0.04]',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          )
        })}
      </div>

      {/* Mode Content */}
      {activeMode === 'intelligence' && (
        <IntelligenceMode
          entityCode={entityCode}
          periodCode={periodCode}
          comparePeriod={comparePeriod}
          onSwitchToExploration={switchToExploration}
        />
      )}

      {activeMode === 'exploration' && (
        <ExplorationMode
          entityCode={entityCode}
          periodCode={periodCode}
          comparePeriod={comparePeriod}
          initialMetric={explorationMetric}
        />
      )}

      {activeMode === 'modeling' && (
        <ModelingMode
          entityCode={entityCode}
          periodCode={periodCode}
        />
      )}
    </div>
  )
}
